/**
 * AdminMembershipsPage - Phase 3H-A (Redesigned)
 *
 * User-centric view for membership management.
 * Shows each user once with pool count, not each membership.
 * 
 * Features:
 * - Users table with pool count
 * - Create User with optional pool assignment
 * - Manage button opens user detail modal
 * - Add/Remove pools per user
 * - Adjust balance per membership
 */

import { useState, useEffect, useCallback } from 'react';
import { useAdminPools } from '../../hooks/useAdminPools';
import { Card, CardHeader, CardTitle, CardContent, Button, LoadingSpinner, Alert, Modal } from '../../components/common';
import { AdminNav } from '../../components/admin';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

interface UserSummary {
  userId: string;
  poolCount: number;
  totalBalance: number;
}

interface UserMembership {
  id: number;
  pool_id: string;
  pool_name: string;
  current_balance: number;
  predictions_made: number;
  is_depleted: boolean;
  joined_at: string | null;
}

export function AdminMembershipsPage(): JSX.Element {
  const { pools } = useAdminPools();

  // Users list state
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  
  // Selected user for management
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [userMemberships, setUserMemberships] = useState<UserMembership[]>([]);
  const [membershipLoading, setMembershipLoading] = useState(false);
  
  // Create user modal
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserPoolId, setNewUserPoolId] = useState('');
  
  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Add to pool state (in manage modal)
  const [addPoolId, setAddPoolId] = useState('');
  
  // Balance adjustment state
  const [adjustMembership, setAdjustMembership] = useState<UserMembership | null>(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');

  const getAuthHeader = () => {
    const userId = localStorage.getItem('current_user_id') || 'larry';
    return { 'X-User-ID': userId };
  };

  // Fetch users list
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await fetch(`${API_BASE}/admin/users`, {
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch user's memberships
  const fetchUserMemberships = useCallback(async (userId: string) => {
    setMembershipLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/memberships?user_id=${userId}&limit=50`, {
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Failed to fetch memberships');
      const data = await response.json();
      setUserMemberships(data.memberships || []);
    } catch (err) {
      console.error('Failed to fetch memberships:', err);
      setUserMemberships([]);
    } finally {
      setMembershipLoading(false);
    }
  }, []);

  // Open manage modal
  const handleManageUser = (user: UserSummary) => {
    setSelectedUser(user);
    fetchUserMemberships(user.userId);
    setAddPoolId('');
    setActionError(null);
  };

  // Create user
  const handleCreateUser = async () => {
    if (!newUserId.trim()) return;
    setActionLoading(true);
    setActionError(null);
    
    try {
      // Create user
      const createResponse = await fetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_id: newUserId.trim(),
          password: newUserPassword || newUserId.trim()  // Default password = user_id
        }),
      });
      
      if (!createResponse.ok) {
        const data = await createResponse.json();
        throw new Error(data.detail || 'Failed to create user');
      }
      
      // Optionally assign to pool
      if (newUserPoolId) {
        const assignResponse = await fetch(`${API_BASE}/admin/pools/${newUserPoolId}/members/${newUserId.trim()}`, {
          method: 'POST',
          headers: getAuthHeader(),
        });
        if (!assignResponse.ok) {
          const data = await assignResponse.json();
          throw new Error(data.detail || 'User created but failed to assign to pool');
        }
      }
      
      setShowCreateUser(false);
      setNewUserId('');
      setNewUserPassword('');
      setNewUserPoolId('');
      setSuccessMessage(`User "${newUserId.trim()}" created successfully${newUserPoolId ? ' and assigned to pool' : ''}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchUsers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setActionLoading(false);
    }
  };

  // Add user to pool
  const handleAddToPool = async () => {
    if (!selectedUser || !addPoolId) return;
    setActionLoading(true);
    setActionError(null);
    
    try {
      const response = await fetch(`${API_BASE}/admin/pools/${addPoolId}/members/${selectedUser.userId}`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to add to pool');
      }
      
      setAddPoolId('');
      setSuccessMessage('Added to pool successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchUserMemberships(selectedUser.userId);
      fetchUsers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add to pool');
    } finally {
      setActionLoading(false);
    }
  };

  // Remove from pool
  const handleRemoveFromPool = async (membership: UserMembership) => {
    if (!confirm(`Remove ${selectedUser?.userId} from ${membership.pool_name}?`)) return;
    setActionLoading(true);
    setActionError(null);
    
    try {
      const response = await fetch(`${API_BASE}/admin/memberships/${membership.id}?reason=Admin removal`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to remove from pool');
      }
      
      setSuccessMessage('Removed from pool successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      if (selectedUser) fetchUserMemberships(selectedUser.userId);
      fetchUsers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove from pool');
    } finally {
      setActionLoading(false);
    }
  };

  // Adjust balance
  const handleAdjustBalance = async () => {
    if (!adjustMembership || !adjustAmount) return;
    setActionLoading(true);
    setActionError(null);
    
    try {
      const response = await fetch(`${API_BASE}/admin/memberships/${adjustMembership.id}/balance`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseInt(adjustAmount),
          reason: adjustReason || 'Admin adjustment',
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to adjust balance');
      }
      
      setAdjustMembership(null);
      setAdjustAmount('');
      setAdjustReason('');
      setSuccessMessage('Balance adjusted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      if (selectedUser) fetchUserMemberships(selectedUser.userId);
      fetchUsers();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to adjust balance');
    } finally {
      setActionLoading(false);
    }
  };

  // Get pools user is NOT in (for add dropdown)
  const availablePools = pools.filter(
    p => !userMemberships.some(m => m.pool_id === p.pool_id)
  );

  return (
    <div className="flex gap-6 p-6">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0">
        <div className="sticky top-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
            Admin
          </h2>
          <AdminNav />
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">User Management</h1>
            <p className="text-slate-400 mt-1">Manage users and their pool assignments</p>
          </div>
          <Button variant="primary" onClick={() => setShowCreateUser(true)}>
            Create User
          </Button>
        </div>

        {/* Messages */}
        {usersError && <Alert variant="error">{usersError}</Alert>}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}

        {/* Users Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Users ({users.length})</CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchUsers}>
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-300">No users found</p>
                <Button variant="primary" className="mt-4" onClick={() => setShowCreateUser(true)}>
                  Create First User
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto bg-slate-900 rounded-lg">
                <table className="w-full">
                  <thead className="bg-slate-800">
                    <tr className="border-b border-slate-600">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-100">User ID</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-100">Pools</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-100">Total Balance</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-100">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-slate-900">
                    {users.map((user) => (
                      <tr
                        key={user.userId}
                        className="border-b border-slate-700 hover:bg-slate-800 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <span className="font-mono text-white">{user.userId}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="px-2 py-1 text-sm bg-slate-700 text-slate-200 rounded">
                            {user.poolCount} pool{user.poolCount !== 1 ? 's' : ''}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-mono text-white">
                            ${user.totalBalance?.toLocaleString() ?? 0}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleManageUser(user)}
                          >
                            Manage
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create User Modal */}
        <Modal
          isOpen={showCreateUser}
          onClose={() => {
            setShowCreateUser(false);
            setNewUserId('');
            setNewUserPoolId('');
            setActionError(null);
          }}
          title="Create New User"
          size="md"
        >
          <div className="space-y-4">
            {actionError && <Alert variant="error">{actionError}</Alert>}
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                User ID *
              </label>
              <input
                type="text"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg
                           text-white placeholder-slate-500
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., player_001"
              />
              <p className="text-xs text-slate-500 mt-1">
                Lowercase with underscores, no spaces
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Password
              </label>
              <input
                type="text"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg
                           text-white placeholder-slate-500
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave blank to use User ID as password"
              />
              <p className="text-xs text-slate-500 mt-1">
                If blank, password defaults to the User ID
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Assign to Pool (optional)
              </label>
              <select
                value={newUserPoolId}
                onChange={(e) => setNewUserPoolId(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg
                           text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" className="bg-slate-800">No initial pool</option>
                {pools.map((p) => (
                  <option key={p.pool_id} value={p.pool_id} className="bg-slate-800">
                    {p.name || p.pool_id} ({p.mode})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
              <Button variant="secondary" onClick={() => setShowCreateUser(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateUser}
                disabled={actionLoading || !newUserId.trim()}
              >
                {actionLoading ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Manage User Modal */}
        <Modal
          isOpen={!!selectedUser}
          onClose={() => {
            setSelectedUser(null);
            setUserMemberships([]);
            setActionError(null);
          }}
          title={`Manage: ${selectedUser?.userId}`}
          size="lg"
        >
          {selectedUser && (
            <div className="space-y-6">
              {actionError && <Alert variant="error">{actionError}</Alert>}
              
              {/* Add to Pool */}
              <div className="p-4 bg-slate-800 rounded-lg">
                <h3 className="text-sm font-medium text-slate-300 mb-3">Add to Pool</h3>
                <div className="flex gap-3">
                  <select
                    value={addPoolId}
                    onChange={(e) => setAddPoolId(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg
                               text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="" className="bg-slate-700">Select a pool...</option>
                    {availablePools.map((p) => (
                      <option key={p.pool_id} value={p.pool_id} className="bg-slate-700">
                        {p.name || p.pool_id} ({p.mode})
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="primary"
                    onClick={handleAddToPool}
                    disabled={actionLoading || !addPoolId}
                  >
                    {actionLoading ? 'Adding...' : 'Add'}
                  </Button>
                </div>
                {availablePools.length === 0 && (
                  <p className="text-xs text-slate-500 mt-2">User is already in all pools</p>
                )}
              </div>

              {/* Current Memberships */}
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-3">
                  Pool Memberships ({userMemberships.length})
                </h3>
                
                {membershipLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : userMemberships.length === 0 ? (
                  <p className="text-slate-400 text-center py-4">Not a member of any pools</p>
                ) : (
                  <div className="space-y-2">
                    {userMemberships.map((m) => (
                      <div
                        key={m.id}
                        className="flex items-center justify-between p-3 bg-slate-800 rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="text-white font-medium">{m.pool_name}</p>
                          <p className="text-xs text-slate-400 font-mono">{m.pool_id}</p>
                        </div>
                        <div className="text-right mr-4">
                          <p className="text-white font-mono">${m.current_balance?.toLocaleString() ?? 0}</p>
                          <p className="text-xs text-slate-400">{m.predictions_made} predictions</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setAdjustMembership(m);
                              setAdjustAmount('');
                              setAdjustReason('');
                            }}
                          >
                            Adjust
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFromPool(m)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            disabled={actionLoading}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-700">
                <Button variant="secondary" onClick={() => setSelectedUser(null)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Adjust Balance Modal */}
        <Modal
          isOpen={!!adjustMembership}
          onClose={() => {
            setAdjustMembership(null);
            setAdjustAmount('');
            setAdjustReason('');
          }}
          title="Adjust Balance"
          size="sm"
        >
          {adjustMembership && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-400">Pool</p>
                <p className="text-white font-medium">{adjustMembership.pool_name}</p>
                <p className="text-sm text-slate-400 mt-2">Current Balance</p>
                <p className="text-white font-mono">${adjustMembership.current_balance?.toLocaleString() ?? 0}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Amount (+/-)
                </label>
                <input
                  type="number"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg
                             text-white placeholder-slate-500
                             focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 500 or -200"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Positive to add, negative to subtract
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg
                             text-white placeholder-slate-500
                             focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Bonus credit"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                <Button variant="secondary" onClick={() => setAdjustMembership(null)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAdjustBalance}
                  disabled={actionLoading || !adjustAmount}
                >
                  {actionLoading ? 'Adjusting...' : 'Adjust Balance'}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </main>
    </div>
  );
}

export default AdminMembershipsPage;
